import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const InstallAppContext = createContext(null);

export const InstallAppProvider = ({ children }) => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);

    useEffect(() => {
        const ua = navigator.userAgent || "";
        const iOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
        setIsIOS(iOS);

        const standalone =
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        setIsInstalled(standalone);

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setIsInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const triggerInstall = useCallback(async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            if (choice.outcome === "accepted") setIsInstalled(true);
            setDeferredPrompt(null);
        } else {
            // iOS Safari + any browser without prompt → show generic instructions
            setShowIOSGuide(true);
        }
    }, [deferredPrompt]);

    const value = {
        canInstall: !isInstalled,
        isInstalled,
        isIOS,
        triggerInstall,
        showIOSGuide,
        dismissIOSGuide: () => setShowIOSGuide(false),
    };
    return <InstallAppContext.Provider value={value}>{children}</InstallAppContext.Provider>;
};

export const useInstallApp = () => {
    const ctx = useContext(InstallAppContext);
    if (!ctx) throw new Error("useInstallApp must be used inside <InstallAppProvider>");
    return ctx;
};
